import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useJobCommsWebSocket } from "@/contexts/JobCommsWebSocketContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Send, Loader2 } from "lucide-react";

// Merge a server message into the list without creating duplicates. On send we append an
// optimistic bubble with a `temp-` id; the WebSocket then echoes the saved message back with
// its real id. Deduping by id alone misses this (temp id != real id), so the echo would render
// as a second identical bubble. Reconcile by replacing the matching optimistic bubble.
const mergeIncomingMessage = (prev, incoming) => {
  if (!incoming) return prev;
  // Already have the real server message — ignore (covers new_message + message_sent both firing).
  if (prev.some((m) => m.id === incoming.id)) return prev;
  // Replace the optimistic bubble we added locally for this same outgoing message.
  const tempIdx = prev.findIndex(
    (m) =>
      typeof m.id === "string" &&
      m.id.startsWith("temp-") &&
      m.sender_id === incoming.sender_id &&
      (m.text || "") === (incoming.text || "")
  );
  if (tempIdx !== -1) {
    const next = [...prev];
    next[tempIdx] = incoming;
    return next;
  }
  return [...prev, incoming];
};

const JobCommsChat = ({ jobId, conversations, userEmail, userId }) => {
  const { isDark } = useTheme();
  const ws = useJobCommsWebSocket();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [otherPerson, setOtherPerson] = useState(null);
  const [typingUser, setTypingUser] = useState(null);
  const messagesEndRef = useRef(null);
  const typingTimeout = useRef(null);
  const typingIndicatorTimeout = useRef(null);

  // Filter to only valid conversations with the actually-hired crew on this confirmed job.
  // Backend tags conversations with `is_hired_crew`; if any are tagged, hide stale applicant
  // conversations from the hire stage. Falls back to all valid conversations if none are tagged
  // (e.g. older data missing the flag).
  const allValid = (conversations || []).filter(
    (conv) => conv.other_person && conv.other_person.name
  );
  const hiredOnly = allValid.filter((conv) => conv.is_hired_crew);
  const validConversations = hiredOnly.length > 0 ? hiredOnly : allValid;

  // Pick first valid conversation
  useEffect(() => {
    if (validConversations.length > 0 && !conversationId) {
      setConversationId(validConversations[0].id);
      setOtherPerson(validConversations[0].other_person);
    }
  }, [validConversations.length]);

  // Load messages for conversation
  useEffect(() => {
    if (!conversationId) {
      setLoading(false);
      return;
    }

    const fetchMessages = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/messages/conversation/${conversationId}?user_email=${userEmail}&limit=100`
        );
        setMessages(res.data.messages || []);
        ws?.markRead(conversationId);
      } catch (err) {
        console.error("Failed to load messages:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, [conversationId, userEmail, ws]);

  // Listen for real-time messages
  useEffect(() => {
    if (!ws) return;

    const unsub1 = ws.on("new_message", (data) => {
      if (data.conversation_id === conversationId || data.job_id === jobId) {
        setMessages((prev) => mergeIncomingMessage(prev, data.message));
        ws.markRead(conversationId);
      }
    });

    const unsub2 = ws.on("message_sent", (data) => {
      if (data.message.conversation_id === conversationId) {
        setMessages((prev) => mergeIncomingMessage(prev, data.message));
      }
    });

    const unsub3 = ws.on("typing", (data) => {
      if (data.job_id === jobId && data.user_id !== userId) {
        setTypingUser(data.user_name);
        clearTimeout(typingIndicatorTimeout.current);
        typingIndicatorTimeout.current = setTimeout(() => setTypingUser(null), 3000);
      }
    });

    const unsub4 = ws.on("stop_typing", (data) => {
      if (data.job_id === jobId) {
        setTypingUser(null);
      }
    });

    return () => {
      unsub1();
      unsub2();
      unsub3();
      unsub4();
    };
  }, [ws, conversationId, jobId, userId]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typingUser]);

  const handleSend = useCallback(async () => {
    if (!inputText.trim() || sending) return;
    const text = inputText.trim();
    setInputText("");
    setSending(true);

    // Optimistic local message
    const optimisticMsg = {
      id: `temp-${Date.now()}`,
      conversation_id: conversationId,
      job_id: jobId,
      sender_id: userId,
      text,
      timestamp: new Date().toISOString(),
      created_at: new Date().toISOString(),
      status: "sending",
    };
    setMessages((prev) => [...prev, optimisticMsg]);

    try {
      if (conversationId && ws?.isConnected) {
        ws.sendMessage(jobId, conversationId, text);
      } else if (conversationId) {
        const senderName =
          JSON.parse(localStorage.getItem("shootzu_user") || "{}")?.firstName || "You";
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/messages/send`, {
          conversation_id: conversationId,
          job_id: jobId,
          sender_id: userId,
          sender_name: senderName,
          receiver_id: otherPerson?.id || "",
          receiver_name: otherPerson?.name || "",
          text,
        });
      }
    } catch (err) {
      console.error("Failed to send:", err);
      // Remove optimistic message on failure
      setMessages((prev) => prev.filter((m) => m.id !== optimisticMsg.id));
      setInputText(text);
    } finally {
      setSending(false);
    }
  }, [inputText, sending, conversationId, ws, jobId, userId, otherPerson]);

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    if (ws?.isConnected) {
      ws.sendTyping(jobId);
      clearTimeout(typingTimeout.current);
      typingTimeout.current = setTimeout(() => {
        ws.sendStopTyping(jobId);
      }, 2000);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isOwnMessage = (msg) => msg.sender_id === userId;

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    try {
      return new Date(timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    try {
      const d = new Date(timestamp);
      const today = new Date();
      if (d.toDateString() === today.toDateString()) return "Today";
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
      return d.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
    } catch {
      return "";
    }
  };

  const groupedMessages = messages.reduce((groups, msg) => {
    const dateStr = formatDate(msg.timestamp || msg.created_at);
    if (!groups[dateStr]) groups[dateStr] = [];
    groups[dateStr].push(msg);
    return groups;
  }, {});

  if (!validConversations || validConversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 text-center" style={{ color: "rgba(255,255,255,.55)" }}>
        <p className="text-lg font-medium mb-2">No conversations yet</p>
        <p className="text-sm">Messages will appear here once a conversation is started for this job.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Conversation selector for multi-crew jobs */}
      {validConversations.length > 1 && (
        <div className="flex gap-2 px-4 py-2.5 overflow-x-auto" style={{ borderBottom: "1px solid rgba(255,255,255,.07)" }}>
          {validConversations.map((conv) => (
            <button
              key={conv.id}
              onClick={() => {
                setConversationId(conv.id);
                setOtherPerson(conv.other_person);
              }}
              className="px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all"
              style={
                conversationId === conv.id
                  ? { background: "#28C0D0", color: "#0C1120" }
                  : { background: "rgba(255,255,255,.06)", color: "rgba(255,255,255,.7)", border: "1px solid rgba(255,255,255,.1)" }
              }
            >
              {conv.other_person?.name || "Unknown"}
            </button>
          ))}
        </div>
      )}

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: "#28C0D0" }} />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full" style={{ color: "rgba(255,255,255,.4)" }}>
            <p className="text-sm">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([dateStr, msgs]) => (
            <div key={dateStr}>
              <div className="flex items-center justify-center my-4">
                <span className="text-xs px-3 py-1 rounded-full" style={{ background: "rgba(255,255,255,.06)", color: "rgba(255,255,255,.5)" }}>
                  {dateStr}
                </span>
              </div>
              {msgs.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex mb-2 ${isOwnMessage(msg) ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className="max-w-[75%] px-4 py-2.5"
                    style={
                      isOwnMessage(msg)
                        ? { background: "#ECEEF2", color: "#10131C", borderRadius: "14px 14px 4px 14px" }
                        : { background: "rgba(255,255,255,.06)", color: "#fff", border: "1px solid rgba(255,255,255,.08)", borderRadius: "14px 14px 14px 4px" }
                    }
                  >
                    {!isOwnMessage(msg) && validConversations.length > 1 && (
                      <p className="text-[10px] font-semibold mb-1" style={{ color: "rgba(255,255,255,.45)" }}>
                        {msg.sender_name}
                      </p>
                    )}
                    <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{msg.text}</p>
                    <p
                      className="text-[10px] mt-1"
                      style={{ color: isOwnMessage(msg) ? "rgba(16,19,28,.5)" : "rgba(255,255,255,.4)" }}
                    >
                      {formatTime(msg.timestamp || msg.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}

        {/* Typing indicator */}
        {typingUser && (
          <div className="flex justify-start mb-2">
            <div className="px-4 py-2" style={{ background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.08)", borderRadius: "14px 14px 14px 4px" }}>
              <p className="text-xs italic" style={{ color: "rgba(255,255,255,.5)" }}>
                {typingUser} is typing...
              </p>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="px-4 py-3" style={{ borderTop: "1px solid rgba(255,255,255,.07)" }}>
        <div
          className="flex items-end gap-2 rounded-2xl pl-4 pr-2 py-1.5"
          style={{ background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.12)" }}
        >
          <textarea
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            placeholder="Message the crew…"
            rows={1}
            className="cd-input-plain flex-1 resize-none bg-transparent border-none px-0 py-2 text-sm max-h-32 focus:outline-none"
            style={{ minHeight: "40px", color: "#fff" }}
          />
          <button
            onClick={handleSend}
            disabled={!inputText.trim() || sending}
            className="cd-send flex items-center justify-center transition-all"
            style={{
              width: 40, height: 40, borderRadius: 11, flex: "0 0 auto",
              ...(inputText.trim() && !sending
                ? { background: "#28C0D0", color: "#0C1120", border: "none" }
                : { background: "rgba(255,255,255,.06)", color: "rgba(255,255,255,.4)", border: "1px solid rgba(255,255,255,.12)" }),
            }}
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobCommsChat;
